import { memo, useState } from 'react'

import i18n from 'i18n-literally'
import PropTypes from 'prop-types'

import { Fingerprint, MailOutlined as MailOutlinedIcon, VpnKey as KeyIcon } from '@mui/icons-material'
import { Box, Divider, IconButton, InputAdornment } from '@mui/material'

import createLogger from '~/src/Lib/Logging'
import PhoneLogin from '~/src/Routes/Login/PhoneLogin'
import { useConnectModern } from '~/src/Store/hooks/useConnectModern'
import Button from '~/src/UI/Shared/Button'
import FormikField from '~/src/UI/Shared/Form/FormikField'
import TextField from '~/src/UI/Shared/Form/TextField'
import Icon from '~/src/UI/Shared/Icon'
import PageTitle from '~/src/UI/Shared/PageTitle'
import T from '~/src/UI/Shared/Typography'

import { PUBLIC_ROUTES, REGISTER_ROUTES } from '../constants'

const displayName = 'LoginForm'
const logger = createLogger(displayName)

const LoginForm = ({
  // alreadyHasAccount,
  email,
  handleGoBack,
  biometric,
  doBiometricLogin,
  isFormValid,
  loginProcessing,
  onSubmit,
  password,
  setState,
  token,
}) => {
  const { isMobile } = useConnectModern('selectIsMobile')
  const [usingPhone, setUsingPhone] = useState(false)
  const fieldMargin = isMobile ? 'dense' : 'normal'
  const stackedSpacing = isMobile ? 2 : 'clamp(1rem, 4.5vh, 2.5rem)'

  if (usingPhone) return <PhoneLogin onCancel={() => setUsingPhone(false)} />

  return (
    <>
      <Box
        data-testid="login-route"
        sx={{
          width: '100%',
          paddingBottom: 3,
        }}
      >
        <PageTitle disablePortal titleText={i18n`Login`} title={i18n`Please login to continue`} align="center" />
      </Box>
      <Box
        aria-label={i18n`AROYA login`}
        component="form"
        onSubmit={onSubmit}
        sx={{
          px: { xs: 0, sm: 'calc(50% - 250px)' },
        }}
      >
        <Box
          component="fieldset"
          sx={{
            border: 'none',
          }}
        >
          {/* Hiding this feature pending update for CORE signup or final decision to remove permanently
        {!alreadyHasAccount && (
          <Box display="flex" gap="1ch" alignItems="center">
            <T.Body.Bold>{i18n`Don't have an account?`}</T.Body.Bold>
              <Button variant="text" href={`${REGISTER_ROUTES.GATEWAY}`}>
                <T.Bold color="primary">{i18n`Create AROYA GO account.`}</T.Bold>
              </Button>
          </Box>
        )}
        */}
          <FormikField
            autoComplete="username"
            margin={fieldMargin}
            required={i18n`Please enter an email address`}
            type="email"
            id="email"
            name="email"
            label={i18n`Email Address`}
            value={email}
            onChange={setState}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={MailOutlinedIcon} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            autoComplete="current-password"
            fullWidth
            required
            margin={fieldMargin}
            id="password"
            name="password"
            type="password"
            label={i18n`Password`}
            value={password}
            onChange={setState}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={KeyIcon} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="text" href={PUBLIC_ROUTES.FORGOT}>
              {i18n`Forgot your password?`}
            </Button>
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: '2rem',
              mt: isMobile ? 1.5 : 3,
              textAlign: 'center',
            }}
          >
            <Button fullWidth type="submit" disabled={loginProcessing || !isFormValid}>
              {i18n`Login`}
            </Button>
            {biometric?.availability === 'available' && biometric.enabled ? (
              <IconButton
                aria-label={i18n`Use ${biometric.label}`}
                disabled={loginProcessing || biometric.processing}
                onClick={() => void doBiometricLogin()}
                size="large"
                type="button"
              >
                <Fingerprint fontSize="large" />
              </IconButton>
            ) : null}
          </Box>
          {token ? (
            <Box sx={{ display: 'flex', mt: '2.5rem', justifyContent: 'center' }}>
              <Button onClick={handleGoBack} variant="text">
                {i18n`Go Back`}
              </Button>
            </Box>
          ) : null}

          <Divider sx={{ mt: stackedSpacing }}>{i18n`or`}</Divider>

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 'clamp(0.5rem, 1.5vh, 1rem)',
              mt: stackedSpacing,
              textAlign: 'center',
            }}
          >
            <T.Body.Bold>{i18n`Continue with Phone`}</T.Body.Bold>
            <Button fullWidth variant="outlined" data-testid="phone-login-button" onClick={() => setUsingPhone(true)}>
              {i18n`Sign in with Phone Number`}
            </Button>
            <T.Tiny
              sx={{ color: 'contrast.muted' }}
            >{i18n`Use your verified mobile number and receive a secure code.`}</T.Tiny>
          </Box>
        </Box>
      </Box>
    </>
  )
}

LoginForm.displayName = displayName
LoginForm.propTypes = {
  // alreadyHasAccount: PropTypes.bool.isRequired,
  email: PropTypes.string.isRequired,
  biometric: PropTypes.shape({
    availability: PropTypes.oneOf(['unknown', 'available', 'unavailable']),
    enabled: PropTypes.bool,
    label: PropTypes.string,
    processing: PropTypes.bool,
  }),
  doBiometricLogin: PropTypes.func,
  handleGoBack: PropTypes.func.isRequired,
  isFormValid: PropTypes.bool.isRequired,
  loginProcessing: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  password: PropTypes.string.isRequired,
  setState: PropTypes.func.isRequired,
  token: PropTypes.bool.isRequired,
}

const Connected = props => {
  const {
    accountCreation: { key: _, ...accountCreation },
    doSetNotHasAccount,
    doUpdateUrl,
  } = useConnectModern('selectAccountCreation', 'doSetNotHasAccount', 'doUpdateUrl')

  const handleGoBack = async () => {
    await doSetNotHasAccount()
    await doUpdateUrl(REGISTER_ROUTES.GATEWAY_SUCCESS)
  }

  return <LoginForm handleGoBack={handleGoBack} {...accountCreation} {...props} />
}

const Memoized = memo(Connected)
export default Memoized
